// @ts-nocheck
import _ from 'lodash';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, Box } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppRoute } from '../../../app';
import { DateUtils, StringUtils } from '../../../../common/utils';
import { DetailsSection, EmptyState } from '../../../../common/components';
import MultiVoucherDetailsSection from './MultiVoucherDetailsSection';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
    padding: theme.spacing(3),
  },
  panel: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(3),
    '&:last-child': {
      marginRight: '0',
    },
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: '0',
    },
  },
  section: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  link: {
    color: 'inherit',
  },
  fill: {
    flex: '1',
  },
}));

function VoucherDetails(props) {
  const [t] = useTranslation();
  const classes = useStyles();

  const { voucher, onDownloadVouchers } = props;

  const formatVoucherState = state => {
    switch (state) {
      case 'ASSIGNED':
        return 'Tildelt';
      case 'UNASSIGNED':
        return 'Ikke tildelt';
      case 'BLOCKED':
        return 'Blokeret';
      default:
        return 'Indløst';
    }
  };

  const getVoucherDetails = () => [
    {
      id: 'voucherCode',
      label: t('voucher.voucherCode'),
      value: voucher.code,
    },
    {
      id: 'voucherState',
      label: t('voucher.voucherState'),
      value: voucher.state,
      formatter: formatVoucherState,
    },
  ];

  const getVoucherRedemption = () => [
    {
      id: 'voucherRedemptionUserId',
      label: t('voucher.voucherRedemptionUserId'),
      value: voucher.redemption.userId,
      formatter: value => (
        <Link to={AppRoute.CustomerDetails.replace(':userId', value)} className={classes.link}>
          {value}
        </Link>
      ),
    },
    {
      id: 'voucherRedemptionName',
      label: t('voucher.voucherRedemptionName'),
      value: voucher.redemption.name,
    },
    {
      id: 'voucherRedemptionOrderId',
      label: t('voucher.voucherRedemptionOrderId'),
      value: voucher.redemption.orderId,
    },
    {
      id: 'voucherRedemptionRedemeedAt',
      label: t('voucher.voucherRedemptionRedemeedAt'),
      value: voucher.redemption.redemeedAt,
      formatter: value => DateUtils.getDate(value),
    },
  ];

  const getVoucherCampaign = () => [
    {
      id: 'voucherCampaignTitle',
      label: t('voucher.voucherCampaignTitle'),
      value: voucher.campaign.title,
    },
    {
      id: 'voucherCampaignDescription',
      label: t('voucher.voucherCampaignDescription'),
      value: voucher.campaign.description,
    },
    {
      id: 'voucherCampaignProduct',
      label: t('voucher.voucherCampaignProduct'),
      value: voucher.campaign.productTitle,
    },
    {
      id: 'voucherCampaignType',
      label: t('voucher.voucherCampaignType'),
      value: voucher.campaign.type,
    },
    {
      id: 'voucherCampaignFlowType',
      label: t('voucher.voucherCampaignFlowType'),
      value: voucher.campaign.flowType,
    },
    {
      id: 'voucherCampaignDiscountType',
      label: t('voucher.voucherCampaignDiscountType'),
      value: voucher.campaign.discountType,
    },
    {
      id: 'voucherCampaignDiscountValue',
      label: t('voucher.voucherCampaignDiscountValue'),
      value: voucher.campaign.discountValue,
      formatter: value => voucher.campaign.discountType === 'PERCENT' ? `${value} %` : StringUtils.getFormattedSum(value),
    },
    {
      id: 'voucherCampaignDiscountDuration',
      label: t('voucher.voucherCampaignDiscountDuration'),
      value: voucher.campaign.discountDuration,
      // formatter: value => value ? `${value} days` : '',
    },
    {
      id: 'voucherCampaignDiscountDays',
      label: t('voucher.voucherCampaignDiscountDays'),
      value: voucher.campaign.discountDays,
    },
    {
      id: 'voucherCampaignAutoCancel',
      label: t('voucher.voucherCampaignAutoCancel'),
      value: voucher.campaign.autoCancel,
      formatter: value => (value ? 'Ja' : 'Nej'),
    },
  ];

  const getVoucherSeries = () => [
    {
      id: 'voucherSeriesSegmentTitle',
      label: t('voucher.voucherSeriesSegmentTitle'),
      value: voucher.series.segment?.title,
    },
    {
      id: 'voucherSeriesTitle',
      label: t('voucher.voucherSeriesTitle'),
      value: voucher.series.title,
    },
    {
      id: 'voucherSeriesDescription',
      label: t('voucher.voucherSeriesDescription'),
      value: voucher.series.description,
    },
    {
      id: 'voucherSeriesPrefix',
      label: t('voucher.voucherSeriesPrefix'),
      value: voucher.series.prefix,
    },
    {
      id: 'voucherSeriesStartDate',
      label: t('voucher.voucherSeriesStartDate'),
      value: voucher.series.startDate,
      valueFormatter: value => DateUtils.getDateTime(value),
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'voucherSeriesEndDate',
      label: t('voucher.voucherSeriesEndDate'),
      value: voucher.series.endDate,
      valueFormatter: value => DateUtils.getDateTime(value),
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'voucherSeriesExported',
      label: t('voucher.voucherSeriesExported'),
      value: voucher.series.exported,
      formatter: value => (value ? 'Ja' : 'Nej'),
    },
    {
      id: 'voucherSeriesRedeemed',
      label: t('voucher.voucherSeriesRedeemed'),
      value: voucher.series.voucherRedeemed,
    },
    {
      id: 'voucherSeriesCount',
      label: t('voucher.voucherSeriesCount'),
      value: voucher.series.voucherCount,
    },
  ];

  if (_.isEmpty(voucher)) {
    return <EmptyState variant="empty" message={t('voucher.voucherEmpty')} />;
  }

  return (
    <Box id="VoucherDetails-container" className={classes.container}>
      <Box className={classes.panel}>
        <Box className={classes.wrapper}>
          <DetailsSection
            id="VoucherDetails"
            title={t('voucher.voucherDetails')}
            className={classes.section}
            data={getVoucherDetails()}
          />
        </Box>
        <Box className={clsx(classes.wrapper, classes.fill)}>
          {voucher.campaign.type === 'MULTI' ? (
            <MultiVoucherDetailsSection
              id="VoucherSeries"
              title={t('voucher.voucherSeries')}
              className={classes.section}
              data={getVoucherSeries()}
              seriesId={voucher.series.id}
              onDownloadVouchers={onDownloadVouchers}
            />
          ) : (
            <DetailsSection
              id="VoucherSeries"
              title={t('voucher.voucherSeries')}
              className={classes.section}
              data={getVoucherSeries()}
            />
          )}
        </Box>
      </Box>
      <Box className={classes.panel}>
        {voucher.redemption && (
          <Box className={classes.wrapper}>
            <DetailsSection
              id="VoucherRedemption"
              title={t('voucher.voucherRedemption')}
              className={classes.section}
              data={getVoucherRedemption()}
            />
          </Box>
        )}
        <Box className={clsx(classes.wrapper, classes.fill)}>
          <DetailsSection
            id="VoucherCampaign"
            title={t('voucher.voucherCampaign')}
            className={classes.section}
            data={getVoucherCampaign()}
          />
        </Box>
      </Box>
    </Box>
  );
}

VoucherDetails.propTypes = {
  voucher: PropTypes.shape({
    code: PropTypes.string,
    state: PropTypes.string,
    redemption: PropTypes.shape({
      name: PropTypes.string,
      userId: PropTypes.string,
      orderId: PropTypes.number,
      redemeedAt: PropTypes.string,
    }),
    series: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      prefix: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      exported: PropTypes.bool,
      campaignId: PropTypes.number,
      voucherCount: PropTypes.number,
      voucherRedeemed: PropTypes.number,
      segment: PropTypes.shape({
        code: PropTypes.string,
        title: PropTypes.string,
      }),
    }),
    campaign: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      type: PropTypes.string,
      flowType: PropTypes.string,
      productId: PropTypes.number,
      discountDays: PropTypes.number,
      discountEndDate: PropTypes.string,
      discountType: PropTypes.string,
      discountValue: PropTypes.number,
      autoCancel: PropTypes.bool,
      productTitle: PropTypes.string,
    }),
  }).isRequired,
  onDownloadVouchers: PropTypes.func.isRequired,
};

export default VoucherDetails;
